import { useCallback, useMemo } from 'react';
import { PreloadJob } from '../../../UnicronApp/preloader/PreloadJob';
import { useOptionalPreloadSlot } from '../../../UnicronApp/preloader/PreloadSlot';
import { useLogger } from '../../services/LoggerService/LoggerService';
import { Fetch, useFetch } from '../../services/NetworkService';
import { useFlags } from '../../services/flags';
import { ChildrenOnly } from '../../types/ChildrenOnly';
import { parseDynamicFromJsonString } from '../../util/Dynamic';
import { env } from '../../util/env';
import { makeApolloClient, NormalClient } from '../shared';
import { HannibalSlot } from './HannibalCacheKey';
import { HannibalContext } from './HannibalContext';

export const HannibalProvider = (props: ChildrenOnly) => {
  const cacheData = useOptionalPreloadSlot(HannibalSlot);
  const flags = useFlags();
  const fetch = useFetch();
  const logger = useLogger();

  const wrappedFetch = useCallback<Fetch>(
    (...args: Parameters<Fetch>) => {
      if (logger && !process.browser) {
        const body = parseDynamicFromJsonString(args[1]?.body);
        logger.warn(`Hannibal cache miss: ${body?.operationName}`);
      }

      return fetch(...args);
    },
    [fetch, logger],
  );

  const client = useMemo(
    () =>
      makeApolloClient(wrappedFetch, env.HANNIBAL_HOST, cacheData, {
        'X-Hannibal-Flags': Object.keys(flags).join('|'),
      }),
    [cacheData, flags, wrappedFetch],
  );

  return <HannibalContext.Provider value={client}>{props.children}</HannibalContext.Provider>;
};

export const hannibalClientForPreload = ({ services }: PreloadJob): NormalClient => {
  const { network, cache, flags, tracingHeaders } = services;

  return makeApolloClient(network.fetch, env.HANNIBAL_HOST, cache.get(HannibalSlot) ?? {}, {
    ...tracingHeaders,
    'X-Hannibal-Flags': Object.keys(flags).join('|'),
  });
};
